import { isDefinedError } from "@orpc/client"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { CircleHelp, ExternalLink, Loader2, RefreshCw } from "lucide-react"
import * as React from "react"
import { toast } from "sonner"
import {
	AttentionCount,
	deltaClass,
	EmptyLine,
	FLUSH_TABLE_CLASS,
	ID_LINK_CLASS,
	Money,
	QueryErrorLine,
	Segmented,
	SkeletonRows,
	StatusView,
	TEXT_LINK_CLASS,
	WRAPPING_PAGE_HEADER_CLASS,
} from "@/components/accounting/accounting-ui"
import {
	BUCKET_LABELS,
	CATEGORY_LABELS,
	EMPTY_VALUE,
	EXCEPTION_KIND_LABELS,
	exceptionStatusPresentation,
	formatDay,
	formatInstant,
	formatMinor,
	monthStatusPresentation,
	parsePeriodKey,
} from "@/components/accounting/reconciliation-format"
import {
	EXCEPTION_STATUS_LABELS,
	type ExceptionResolution,
	exceptionKindGuidance,
	RESOLUTION_LABELS,
	RESOLUTION_OPTIONS,
} from "@/components/accounting/reconciliation-guidance"
import {
	DetailPanel,
	DetailPanelSection,
	PageHeader,
	Panel,
	Toolbar,
	ToolbarSpacer,
} from "@/components/layout"
import { Button } from "@/components/ui/button"
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover"
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select"
import { StatusDot, StatusTag } from "@/components/ui/status"
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Textarea } from "@/components/ui/textarea"
import { createTabSearch } from "@/hooks/use-url-tab"
import {
	type AnnotateExceptionInput,
	type ListLedgerInput,
	type ListReconciliationExceptionsInput,
	type ReconciliationRegion,
	useAnnotateException,
	useLedgerSearch,
	useReconciliationExceptions,
	useReconciliationMonths,
	useReconciliationStatement,
	useRematchMonth,
} from "@/queries/marketplace/use-stripe-reconciliation"

const TABS = ["statement", "composition", "exceptions", "ledger"] as const
type ReconciliationTab = (typeof TABS)[number]

export const Route = createFileRoute(
	"/accounting/reconciliation/$region/$period",
)({
	component: ReconciliationPeriodPage,
	validateSearch: createTabSearch(TABS),
})

const REGIONS: ReconciliationRegion[] = ["EU", "NA"]

const BUCKETS = [
	"open",
	"awaiting_payout_hold",
	"awaiting_payout_overdue",
	"closed_same_currency_retained",
	"closed_cross_currency_funding",
	"disputes",
	"top_ups",
	"prefunding_reserve",
	"unattributable_fees",
	"bank_payouts",
	"unallocated",
] as const

type StatementData = NonNullable<
	ReturnType<typeof useReconciliationStatement>["data"]
>
type StatementBlock = StatementData["blocks"][number]
type StatementException = StatementData["exceptions"][number]
type CompositionBlock = StatementData["composition"][number]

function isRegion(value: string): value is ReconciliationRegion {
	return (REGIONS as string[]).includes(value)
}

function blockTitle(block: { currency: string; balanceType: string }): string {
	return block.balanceType === "refund_and_dispute_prefunding"
		? `${block.currency} · refund and dispute prefunding reserve`
		: `${block.currency} · payments balance`
}

function ReconciliationPeriodPage() {
	const { region, period } = Route.useParams()
	const searchParams = Route.useSearch()
	const navigate = useNavigate()
	const parsed = parsePeriodKey(period)

	if (!isRegion(region) || !parsed) {
		return (
			<div className="p-4 sm:p-6">
				<p className="rounded-lg bg-bad-soft p-4 text-bad">
					Unknown region or period: {region} / {period}. Use EU or NA and a
					YYYY-MM month.
				</p>
			</div>
		)
	}

	const activeTab = searchParams.tab ?? "statement"
	const setActiveTab = (tab: string) => {
		navigate({
			to: "/accounting/reconciliation/$region/$period",
			params: { region, period },
			search: tab === "statement" ? {} : { tab: tab as ReconciliationTab },
			replace: true,
		})
	}

	return (
		<PeriodContent
			region={region}
			period={period}
			year={parsed.year}
			month={parsed.month}
			activeTab={activeTab}
			onTabChange={setActiveTab}
		/>
	)
}

interface PeriodContentProps {
	region: ReconciliationRegion
	period: string
	year: number
	month: number
	activeTab: ReconciliationTab
	onTabChange: (tab: string) => void
}

function PeriodContent({
	region,
	period,
	year,
	month,
	activeTab,
	onTabChange,
}: PeriodContentProps) {
	const statement = useReconciliationStatement({ region, year, month })
	const months = useReconciliationMonths(region)
	const rematch = useRematchMonth()
	const monthRow = months.data?.months.find((m) => m.period === period)

	const runRematch = async () => {
		const { error, data } = await rematch.mutateAsync({ region, year, month })
		if (error) {
			if (isDefinedError(error) && error.code === "PERIOD_FINALISED") {
				toast.error("This month is finalised; rematching is refused.")
				return
			}
			toast.error(
				error instanceof Error
					? `Rematch failed: ${error.message}`
					: "Rematch failed.",
			)
			return
		}
		toast.success(
			`${data.rematched} rows rematched: ${data.matched} matched, ${data.partial} partial, ${data.unmatched} unmatched, ${data.stillOpen} exceptions still open.`,
		)
	}

	const header = statement.data?.header

	return (
		<div className="flex flex-col gap-6 p-4 sm:p-6">
			<PageHeader
				className={WRAPPING_PAGE_HEADER_CLASS}
				title={
					<span className="inline-flex items-center gap-3">
						<span className="font-mono">{period}</span>
						{monthRow ? (
							<StatusView
								presentation={monthStatusPresentation(monthRow.status)}
								className="text-sm font-medium tracking-normal"
							/>
						) : null}
						{header?.frozen ? (
							<StatusDot className="text-sm font-medium tracking-normal">
								Frozen
							</StatusDot>
						) : null}
					</span>
				}
				description={
					<span className="flex flex-col gap-1.5">
						<span>
							<Link to="/accounting/reconciliation" className={TEXT_LINK_CLASS}>
								Stripe reconciliation
							</Link>{" "}
							{region}
							<span className="text-meta"> · </span>
							{header
								? `${formatInstant(header.from)} → ${formatInstant(header.to)}`
								: "Europe/Paris calendar month"}
							{header ? (
								<>
									<span className="text-meta"> · </span>
									synced through {formatInstant(header.syncedThrough)}
								</>
							) : null}
						</span>
						{header ? (
							<span className="flex flex-wrap items-center gap-x-4 gap-y-1">
								{header.anchor ? (
									header.anchor.balanced ? (
										<StatusDot tone="ok">Anchor ties</StatusDot>
									) : (
										<StatusTag tone="bad">Anchor mismatch</StatusTag>
									)
								) : (
									<StatusDot>No anchor</StatusDot>
								)}
								{header.rollForwardBalanced ? (
									<StatusDot tone="ok">Rolls forward</StatusDot>
								) : (
									<StatusTag tone="bad">Roll-forward break</StatusTag>
								)}
								{header.openExceptions > 0 ? (
									<StatusTag tone="bad">
										{header.openExceptions} open exceptions
									</StatusTag>
								) : (
									<StatusDot tone="ok">0 open exceptions</StatusDot>
								)}
							</span>
						) : null}
					</span>
				}
				actions={
					<Button
						variant="outline"
						onClick={runRematch}
						disabled={rematch.isPending || header?.frozen}
					>
						{rematch.isPending ? (
							<Loader2 className="animate-spin" />
						) : (
							<RefreshCw />
						)}
						Rematch month
					</Button>
				}
			/>

			<Tabs value={activeTab} onValueChange={onTabChange} className="gap-6">
				<TabsList variant="line">
					<TabsTrigger value="statement">Statement</TabsTrigger>
					<TabsTrigger value="composition">Composition</TabsTrigger>
					<TabsTrigger value="exceptions">Exceptions</TabsTrigger>
					<TabsTrigger value="ledger">Ledger</TabsTrigger>
				</TabsList>

				<TabsContent value="statement" className="mt-0">
					<StatementTab statement={statement} />
				</TabsContent>
				<TabsContent value="composition" className="mt-0">
					{statement.data ? (
						<CompositionTab blocks={statement.data.composition} />
					) : (
						<StatementState statement={statement} />
					)}
				</TabsContent>
				<TabsContent value="exceptions" className="mt-0">
					<ExceptionsTab region={region} year={year} month={month} />
				</TabsContent>
				<TabsContent value="ledger" className="mt-0">
					<LedgerTab region={region} year={year} month={month} />
				</TabsContent>
			</Tabs>
		</div>
	)
}

function StatementState({
	statement,
}: {
	statement: ReturnType<typeof useReconciliationStatement>
}) {
	if (statement.isLoading) {
		return (
			<Panel>
				<SkeletonRows rows={4} />
			</Panel>
		)
	}
	if (statement.isError) {
		const error = statement.error
		if (isDefinedError(error) && error.code === "PERIOD_NOT_SYNCED") {
			return (
				<div className="flex flex-col gap-1">
					<p className="font-medium">The ledger has not reached this month.</p>
					<p className="text-muted-foreground">
						Synced through {formatInstant(error.data.syncedThrough)}. Run a sync
						or a backfill from the reconciliation page first.
					</p>
				</div>
			)
		}
		return <QueryErrorLine what="the statement" error={error} />
	}
	return null
}

function StatementTab({
	statement,
}: {
	statement: ReturnType<typeof useReconciliationStatement>
}) {
	if (!statement.data) {
		return <StatementState statement={statement} />
	}
	const data = statement.data
	return (
		<div className="flex min-w-0 flex-col gap-4">
			{data.blocks.map((block) => (
				<StatementBlockPanel
					key={`${block.currency}-${block.balanceType}`}
					block={block}
				/>
			))}
			{data.blocks.length === 0 ? (
				<p className="text-muted-foreground">
					No balance transaction in this month.
				</p>
			) : null}

			<DetailPanel
				title="No Stripe object expected"
				badges={
					<span className="text-sm text-meta tabular-nums">
						{data.noStripeObjectExpected.count}
					</span>
				}
				meta="Succeeded transactions with a zero total (100 % coupon carts). They have no charge on Stripe and are not exceptions."
			>
				<DetailPanelSection>
					{data.noStripeObjectExpected.count === 0 ? (
						<p className="text-meta">None this month.</p>
					) : (
						<div className="flex flex-wrap gap-x-4 gap-y-1">
							{data.noStripeObjectExpected.transactions.map((tx) => (
								<Link
									key={tx.transactionId}
									to="/transactions/$transactionId"
									params={{ transactionId: tx.transactionId }}
									className={ID_LINK_CLASS}
								>
									{tx.transactionNumber}{" "}
									<span className="font-sans text-meta">
										{formatDay(tx.paidAt)}
									</span>
								</Link>
							))}
						</div>
					)}
				</DetailPanelSection>
			</DetailPanel>

			{data.priorPeriodItems.length > 0 ? (
				<DetailPanel
					title="Prior-period items"
					meta="Exceptions raised this month whose date falls in a month that is already finalised. The finalised month is not changed."
					flush
				>
					<ExceptionsTable
						items={data.priorPeriodItems}
						onAnnotate={undefined}
						className={FLUSH_TABLE_CLASS}
					/>
				</DetailPanel>
			) : null}
		</div>
	)
}

function StatementBlockPanel({ block }: { block: StatementBlock }) {
	const currency = block.currency
	return (
		<DetailPanel title={blockTitle(block)}>
			<DetailPanelSection>
				<dl className="m-0 flex flex-wrap gap-x-8 gap-y-2">
					<Figure label="Opening">
						<Money cents={block.opening} currency={currency} />
					</Figure>
					<Figure label="Σ movements">
						<Money cents={block.movements} currency={currency} />
					</Figure>
					<Figure label="Closing">
						<Money
							cents={block.closing}
							currency={currency}
							className="font-semibold"
						/>
					</Figure>
					<Figure label="Closing available">
						<Money cents={block.closingAvailable} currency={currency} />
					</Figure>
					<Figure label="Roll-forward delta">
						<Money
							cents={block.rollForwardDelta}
							currency={currency}
							className={deltaClass(block.rollForwardDelta)}
						/>
					</Figure>
				</dl>
			</DetailPanelSection>
			<Table className={FLUSH_TABLE_CLASS}>
				<TableHeader>
					<TableRow>
						<TableHead>Category</TableHead>
						<TableHead className="text-right">Stripe count</TableHead>
						<TableHead className="text-right">Stripe gross</TableHead>
						<TableHead className="text-right">Stripe fee</TableHead>
						<TableHead className="text-right">Stripe net</TableHead>
						<TableHead className="text-right">DB count</TableHead>
						<TableHead className="text-right">DB amount</TableHead>
						<TableHead className="text-right">Δ count</TableHead>
						<TableHead className="text-right">Δ amount</TableHead>
						<TableHead className="text-right">Open</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{block.lines.map((line) => (
						<React.Fragment key={line.category}>
							<TableRow>
								<TableCell>
									<div className="font-medium">
										{CATEGORY_LABELS[line.category] ?? line.category}
									</div>
									<div className="text-xs text-meta">
										{line.stripeOnly
											? "no DB counterpart"
											: [
													line.amountMismatches > 0
														? `${line.amountMismatches} amount mismatch`
														: null,
													line.partialMatches > 0
														? `${line.partialMatches} partial`
														: null,
													line.unmatched > 0
														? `${line.unmatched} unmatched`
														: null,
													line.missingInStripe > 0
														? `${line.missingInStripe} missing in Stripe`
														: null,
												]
													.filter(Boolean)
													.join(" · ") || "1-for-1"}
									</div>
								</TableCell>
								<TableCell className="text-right tabular-nums">
									{line.stripeCount}
								</TableCell>
								<TableCell className="text-right">
									<Money cents={line.stripeGross} currency={currency} />
								</TableCell>
								<TableCell className="text-right text-muted-foreground">
									<Money cents={line.stripeFee} currency={currency} />
								</TableCell>
								<TableCell className="text-right">
									<Money cents={line.stripeNet} currency={currency} />
								</TableCell>
								<TableCell className="text-right tabular-nums">
									{line.stripeOnly ? EMPTY_VALUE : line.dbCount}
								</TableCell>
								<TableCell className="text-right">
									{line.stripeOnly ? (
										EMPTY_VALUE
									) : (
										<Money cents={line.dbAmount} currency={currency} />
									)}
								</TableCell>
								<TableCell
									className={`text-right tabular-nums ${line.stripeOnly ? "" : deltaClass(line.countDelta)}`}
								>
									{line.stripeOnly ? EMPTY_VALUE : line.countDelta}
								</TableCell>
								<TableCell className="text-right">
									{line.stripeOnly ? (
										EMPTY_VALUE
									) : (
										<Money
											cents={line.amountDelta}
											currency={currency}
											className={deltaClass(line.amountDelta)}
										/>
									)}
								</TableCell>
								<TableCell className="text-right">
									<AttentionCount count={line.openExceptions} />
								</TableCell>
							</TableRow>
							{line.subtypes.length > 1
								? line.subtypes.map((subtype) => (
										<TableRow
											key={`${line.category}-${subtype.subtype}`}
											className="text-xs text-muted-foreground"
										>
											<TableCell className="pl-8 font-mono">
												{subtype.subtype}
											</TableCell>
											<TableCell className="text-right tabular-nums">
												{subtype.count}
											</TableCell>
											<TableCell className="text-right">
												<Money cents={subtype.gross} currency={currency} />
											</TableCell>
											<TableCell className="text-right">
												<Money cents={subtype.fee} currency={currency} />
											</TableCell>
											<TableCell className="text-right">
												<Money cents={subtype.net} currency={currency} />
											</TableCell>
											<TableCell colSpan={5} />
										</TableRow>
									))
								: null}
						</React.Fragment>
					))}
					<TableRow>
						<TableCell>
							<div className="font-medium">
								Processing fees embedded in charges
							</div>
							<div className="text-xs text-meta">
								Σ fee of the charge rows vs Σ transactions.stripe_fees
							</div>
						</TableCell>
						<TableCell colSpan={2} />
						<TableCell className="text-right">
							<Money cents={block.processingFees.stripe} currency={currency} />
						</TableCell>
						<TableCell colSpan={2} />
						<TableCell className="text-right">
							<Money cents={block.processingFees.db} currency={currency} />
						</TableCell>
						<TableCell />
						<TableCell className="text-right">
							<Money
								cents={block.processingFees.delta}
								currency={currency}
								className={deltaClass(block.processingFees.delta)}
							/>
						</TableCell>
						<TableCell />
					</TableRow>
				</TableBody>
			</Table>
		</DetailPanel>
	)
}

function Figure({
	label,
	children,
}: {
	label: string
	children: React.ReactNode
}) {
	return (
		<div className="flex flex-col">
			<dt className="text-xs text-meta">{label}</dt>
			<dd className="m-0 tabular-nums">{children}</dd>
		</div>
	)
}

function CompositionTab({ blocks }: { blocks: CompositionBlock[] }) {
	if (blocks.length === 0) {
		return (
			<p className="text-muted-foreground">
				No balance at the end of this month.
			</p>
		)
	}
	return (
		<div className="flex min-w-0 flex-col gap-4">
			<p className="max-w-3xl text-muted-foreground">
				Where every cent of the closing balance sits at month end. Order lines
				follow the order's position at that instant, from its status history and
				payout; platform lines hold what no order owns. Each block sums exactly
				to its closing balance. Cross-currency orders show their charge in the
				buyer's currency and their payout funding in EUR.
			</p>
			<div className="grid items-start gap-4 xl:grid-cols-2">
				{blocks.map((block) => (
					<DetailPanel
						key={`${block.currency}-${block.balanceType}`}
						title={blockTitle(block)}
						flush
					>
						<Table className={FLUSH_TABLE_CLASS}>
							<TableHeader>
								<TableRow>
									<TableHead>Line</TableHead>
									<TableHead className="text-right">Amount</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{BUCKETS.map((bucket) => {
									const amount = block.buckets[bucket] ?? 0
									if (amount === 0) return null
									return (
										<TableRow key={bucket}>
											<TableCell>{BUCKET_LABELS[bucket] ?? bucket}</TableCell>
											<TableCell className="text-right">
												<Money cents={amount} currency={block.currency} />
											</TableCell>
										</TableRow>
									)
								})}
								<TableRow className="font-semibold">
									<TableCell>Closing</TableCell>
									<TableCell className="text-right">
										<Money cents={block.closing} currency={block.currency} />
									</TableCell>
								</TableRow>
							</TableBody>
						</Table>
					</DetailPanel>
				))}
			</div>
		</div>
	)
}

function ExceptionsTab({
	region,
	year,
	month,
}: {
	region: ReconciliationRegion
	year: number
	month: number
}) {
	const [side, setSide] = React.useState<"all" | "stripe" | "db">("all")
	const [status, setStatus] = React.useState<
		"all" | "open" | "explained" | "resolved"
	>("open")
	const [cursor, setCursor] = React.useState<string | undefined>(undefined)
	const [annotating, setAnnotating] = React.useState<StatementException | null>(
		null,
	)
	const input: ListReconciliationExceptionsInput = {
		region,
		year,
		month,
		side: side === "all" ? undefined : side,
		status: status === "all" ? undefined : status,
		limit: 50,
		cursor,
	}
	const exceptions = useReconciliationExceptions(input)

	return (
		<div className="flex flex-col gap-4">
			<Toolbar>
				<Segmented
					ariaLabel="Side"
					value={side}
					onChange={(value) => {
						setSide(value)
						setCursor(undefined)
					}}
					options={[
						{ value: "all", label: "Both sides" },
						{ value: "stripe", label: "Stripe rows" },
						{ value: "db", label: "DB facts" },
					]}
				/>
				<Select
					value={status}
					onValueChange={(v) => {
						setStatus(v as typeof status)
						setCursor(undefined)
					}}
				>
					<SelectTrigger aria-label="Status" className="w-52">
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="all">Any status</SelectItem>
						<SelectItem value="open">{EXCEPTION_STATUS_LABELS.open}</SelectItem>
						<SelectItem value="explained">
							{EXCEPTION_STATUS_LABELS.explained}
						</SelectItem>
						<SelectItem value="resolved">
							{EXCEPTION_STATUS_LABELS.resolved}
						</SelectItem>
					</SelectContent>
				</Select>
				{exceptions.data ? (
					<span className="text-xs text-meta tabular-nums">
						{exceptions.data.total} exceptions
					</span>
				) : null}
				<ToolbarSpacer />
				<ExceptionsHelp />
			</Toolbar>

			{exceptions.isLoading ? (
				<Panel>
					<SkeletonRows rows={4} />
				</Panel>
			) : exceptions.isError ? (
				<QueryErrorLine what="the exceptions" error={exceptions.error} />
			) : (exceptions.data?.items.length ?? 0) === 0 ? (
				<p className="text-muted-foreground">
					No exception matches these filters.
				</p>
			) : (
				<>
					<Panel>
						<ExceptionsTable
							items={exceptions.data?.items ?? []}
							onAnnotate={setAnnotating}
						/>
					</Panel>
					{exceptions.data?.nextCursor ? (
						<div>
							<Button
								variant="outline"
								size="sm"
								onClick={() =>
									setCursor(exceptions.data?.nextCursor ?? undefined)
								}
							>
								Next page
							</Button>
						</div>
					) : null}
				</>
			)}

			<AnnotateDialog
				exception={annotating}
				onClose={() => setAnnotating(null)}
			/>
		</div>
	)
}

function ExceptionsTable({
	items,
	onAnnotate,
	className,
}: {
	items: StatementException[]
	onAnnotate: ((exception: StatementException) => void) | undefined
	className?: string
}) {
	if (items.length === 0) {
		return <EmptyLine>No exception matches these filters.</EmptyLine>
	}
	return (
		<Table className={className}>
			<TableHeader>
				<TableRow>
					<TableHead>Date</TableHead>
					<TableHead>Side</TableHead>
					<TableHead>Kind</TableHead>
					<TableHead>Subject</TableHead>
					<TableHead>Stripe id</TableHead>
					<TableHead className="text-right">Amount</TableHead>
					<TableHead>Status</TableHead>
					<TableHead>Note</TableHead>
					{onAnnotate ? <TableHead /> : null}
				</TableRow>
			</TableHeader>
			<TableBody>
				{items.map((exception) => (
					<TableRow key={exception.id}>
						<TableCell className="whitespace-nowrap text-xs text-muted-foreground">
							{formatInstant(exception.occurredAt)}
						</TableCell>
						<TableCell className="text-muted-foreground">
							{exception.side}
						</TableCell>
						<TableCell>
							{EXCEPTION_KIND_LABELS[exception.kind] ?? exception.kind}
						</TableCell>
						<TableCell>
							<div className="flex flex-col gap-0.5">
								{exception.subjectTransactionId ? (
									<Link
										to="/transactions/$transactionId"
										params={{ transactionId: exception.subjectTransactionId }}
										className={ID_LINK_CLASS}
									>
										{exception.subjectTransactionNumber ??
											exception.subjectTransactionId}
									</Link>
								) : null}
								{exception.subjectOrderId ? (
									<Link
										to="/orders/$orderId"
										params={{ orderId: exception.subjectOrderId }}
										className={ID_LINK_CLASS}
									>
										{exception.subjectOrderNumber ?? exception.subjectOrderId}
									</Link>
								) : null}
								{!exception.subjectTransactionId &&
								!exception.subjectOrderId ? (
									<span className="font-mono">{exception.subjectKey}</span>
								) : null}
							</div>
						</TableCell>
						<TableCell>
							{exception.links.stripe ? (
								<a
									href={exception.links.stripe}
									target="_blank"
									rel="noreferrer"
									className={`inline-flex items-center gap-1 ${ID_LINK_CLASS}`}
								>
									{exception.stripeId ?? exception.expectedStripeId}
									<ExternalLink className="size-3 text-meta" />
								</a>
							) : (
								<span className="text-meta">{EMPTY_VALUE}</span>
							)}
						</TableCell>
						<TableCell className="text-right">
							<Money cents={exception.amount} currency={exception.currency} />
						</TableCell>
						<TableCell>
							<div className="flex flex-col items-start gap-0.5">
								<StatusView
									presentation={{
										...exceptionStatusPresentation(exception.status),
										label:
											EXCEPTION_STATUS_LABELS[exception.status] ??
											exception.status,
									}}
								/>
								{exception.resolution ? (
									<span className="text-xs text-meta">
										{RESOLUTION_LABELS[exception.resolution] ??
											exception.resolution}
									</span>
								) : null}
							</div>
						</TableCell>
						<TableCell className="max-w-xs text-xs">
							{exception.note ?? (
								<span className="text-meta">{EMPTY_VALUE}</span>
							)}
							{exception.resolvedBy ? (
								<div className="text-meta">by {exception.resolvedBy}</div>
							) : null}
						</TableCell>
						{onAnnotate ? (
							<TableCell className="text-right">
								<Button
									variant="outline"
									size="sm"
									onClick={() => onAnnotate(exception)}
								>
									Annotate
								</Button>
							</TableCell>
						) : null}
					</TableRow>
				))}
			</TableBody>
		</Table>
	)
}

/** How to work through the exceptions of a month, behind a toolbar button. */
function ExceptionsHelp() {
	return (
		<Popover>
			<PopoverTrigger asChild>
				<Button variant="ghost" size="sm">
					<CircleHelp />
					How to work through the exceptions
				</Button>
			</PopoverTrigger>
			<PopoverContent
				align="end"
				className="flex w-[min(480px,calc(100vw-2rem))] flex-col gap-3"
			>
				<p>
					An exception is a Stripe movement that our database does not explain,
					or a database fact that Stripe has not posted. Every exception dated
					in the month must be explained before the month can be finalised.
					Explaining never changes a figure: it records why the difference is
					legitimate, or that the fact has been corrected on our side.
				</p>
				<ol className="flex list-decimal flex-col gap-1 pl-5">
					<li>
						Open the Stripe link and the transaction or order to see what the
						movement is. Empty Stripe metadata means the movement was created
						outside the marketplace.
					</li>
					<li>
						Press Annotate, pick the resolution that fits, and write a note a
						reader will understand next year.
					</li>
					<li>
						Open means the month is blocked; explained means a person has
						answered; resolved means a later sync closed it on its own (your
						note is kept, and the row reopens if the same problem comes back).
					</li>
				</ol>
				<dl className="m-0 grid gap-x-4 gap-y-2 sm:grid-cols-[max-content_1fr]">
					{RESOLUTION_OPTIONS.map((option) => (
						<React.Fragment key={option.value}>
							<dt className="font-medium">{option.label}</dt>
							<dd className="m-0 text-muted-foreground">{option.when}</dd>
						</React.Fragment>
					))}
				</dl>
			</PopoverContent>
		</Popover>
	)
}

/** What this kind of exception means and what to check before annotating it. */
function KindGuidance({ exception }: { exception: StatementException }) {
	const guidance = exceptionKindGuidance(exception.kind)
	const detectionNote =
		exception.status === "open" && exception.note ? exception.note : null
	return (
		<div className="flex flex-col gap-2 rounded-md bg-muted p-3">
			<p>{guidance.meaning}</p>
			{detectionNote ? (
				<p>
					<span className="font-medium">Detected as: </span>
					{detectionNote}
				</p>
			) : null}
			<p className="text-muted-foreground">{guidance.check}</p>
			<div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
				{exception.links.stripe ? (
					<a
						href={exception.links.stripe}
						target="_blank"
						rel="noreferrer"
						className={`inline-flex items-center gap-1 ${TEXT_LINK_CLASS}`}
					>
						Open on Stripe
						<ExternalLink className="size-3 text-meta" />
					</a>
				) : null}
				{exception.subjectTransactionId ? (
					<Link
						to="/transactions/$transactionId"
						params={{ transactionId: exception.subjectTransactionId }}
						className={TEXT_LINK_CLASS}
					>
						Open transaction{" "}
						{exception.subjectTransactionNumber ??
							exception.subjectTransactionId}
					</Link>
				) : null}
				{exception.subjectOrderId ? (
					<Link
						to="/orders/$orderId"
						params={{ orderId: exception.subjectOrderId }}
						className={TEXT_LINK_CLASS}
					>
						Open order{" "}
						{exception.subjectOrderNumber ?? exception.subjectOrderId}
					</Link>
				) : null}
			</div>
		</div>
	)
}

/** When to use the selected resolution and what it does to the close. */
function ResolutionGuidance({
	resolution,
}: {
	resolution: ExceptionResolution
}) {
	const option = RESOLUTION_OPTIONS.find((o) => o.value === resolution)
	if (!option) return null
	return (
		<div className="text-xs text-muted-foreground flex flex-col gap-1">
			<p>
				<span className="font-medium text-foreground">Use it when: </span>
				{option.when}
			</p>
			<p>
				<span className="font-medium text-foreground">Effect: </span>
				{option.effect}
			</p>
		</div>
	)
}

function AnnotateDialog({
	exception,
	onClose,
}: {
	exception: StatementException | null
	onClose: () => void
}) {
	const annotate = useAnnotateException()
	const [note, setNote] = React.useState("")
	const [resolution, setResolution] =
		React.useState<NonNullable<AnnotateExceptionInput["resolution"]>>(
			"explained",
		)
	const [status, setStatus] =
		React.useState<AnnotateExceptionInput["status"]>("explained")

	React.useEffect(() => {
		if (exception) {
			setNote(exception.note ?? "")
			setResolution(
				exception.resolution ?? exceptionKindGuidance(exception.kind).suggested,
			)
			setStatus(exception.status === "explained" ? "explained" : "explained")
		}
	}, [exception])

	const submit = async () => {
		if (!exception) return
		const { error } = await annotate.mutateAsync({
			id: exception.id,
			note,
			resolution: status === "explained" ? resolution : null,
			status,
		})
		if (error) {
			if (isDefinedError(error) && error.code === "PERIOD_FINALISED") {
				toast.error("This month is finalised; the exception cannot change.")
				return
			}
			if (isDefinedError(error) && error.code === "EXCEPTION_NOT_FOUND") {
				toast.error("This exception no longer exists.")
				onClose()
				return
			}
			toast.error(
				error instanceof Error
					? `Could not save the note: ${error.message}`
					: "Could not save the note.",
			)
			return
		}
		toast.success(
			status === "explained" ? "Exception explained." : "Exception reopened.",
		)
		onClose()
	}

	return (
		<Dialog
			open={exception !== null}
			onOpenChange={(open) => !open && onClose()}
		>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>Annotate exception</DialogTitle>
					<DialogDescription>
						{exception
							? `${EXCEPTION_KIND_LABELS[exception.kind] ?? exception.kind} · ${formatMinor(exception.amount, exception.currency)} · ${formatInstant(exception.occurredAt)}`
							: null}
					</DialogDescription>
				</DialogHeader>
				{exception ? <KindGuidance exception={exception} /> : null}
				<div className="flex flex-col gap-4">
					<div className="flex flex-col gap-1">
						<Label htmlFor="annotate-status">Outcome</Label>
						<Select
							value={status}
							onValueChange={(v) => setStatus(v as typeof status)}
						>
							<SelectTrigger id="annotate-status">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="explained">
									Explained: the month can be finalised
								</SelectItem>
								<SelectItem value="open">
									Keep open: leave it for someone else, the month stays blocked
								</SelectItem>
							</SelectContent>
						</Select>
					</div>
					{status === "explained" ? (
						<div className="flex flex-col gap-1">
							<Label htmlFor="annotate-resolution">Resolution</Label>
							<Select
								value={resolution}
								onValueChange={(v) => setResolution(v as typeof resolution)}
							>
								<SelectTrigger id="annotate-resolution">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{RESOLUTION_OPTIONS.map((option) => (
										<SelectItem key={option.value} value={option.value}>
											{option.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							<ResolutionGuidance resolution={resolution} />
						</div>
					) : null}
					<div className="flex flex-col gap-1">
						<Label htmlFor="annotate-note">Note</Label>
						<Textarea
							id="annotate-note"
							value={note}
							onChange={(e) => setNote(e.target.value)}
							rows={4}
							placeholder={
								exception
									? exceptionKindGuidance(exception.kind).example
									: "What this movement is and how it is booked"
							}
						/>
					</div>
				</div>
				<DialogFooter>
					<Button variant="outline" onClick={onClose}>
						Cancel
					</Button>
					<Button
						onClick={submit}
						disabled={annotate.isPending || note.trim().length === 0}
					>
						{annotate.isPending ? (
							<Loader2 className="mr-2 h-4 w-4 animate-spin" />
						) : null}
						Save
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}

function matchStatusView(matchStatus: string) {
	if (matchStatus === "matched") {
		return <StatusDot tone="ok">Matched</StatusDot>
	}
	if (matchStatus === "not_applicable") {
		return <StatusDot>Not applicable</StatusDot>
	}
	if (matchStatus === "partial") {
		return <StatusDot tone="warn">Partial</StatusDot>
	}
	if (matchStatus === "unmatched") {
		return <StatusTag tone="bad">Unmatched</StatusTag>
	}
	return <StatusDot>{matchStatus}</StatusDot>
}

function LedgerTab({
	region,
	year,
	month,
}: {
	region: ReconciliationRegion
	year: number
	month: number
}) {
	const [searchText, setSearchText] = React.useState("")
	const [search, setSearch] = React.useState("")
	const [category, setCategory] = React.useState<string>("all")
	const [matchStatus, setMatchStatus] = React.useState<string>("all")
	const [cursor, setCursor] = React.useState<string | undefined>(undefined)
	const wholeLedger = search.length > 0
	const input: ListLedgerInput = {
		region,
		year: wholeLedger ? undefined : year,
		month: wholeLedger ? undefined : month,
		category:
			category === "all"
				? undefined
				: (category as ListLedgerInput["category"]),
		matchStatus:
			matchStatus === "all"
				? undefined
				: (matchStatus as ListLedgerInput["matchStatus"]),
		search: search || undefined,
		limit: 50,
		cursor,
	}
	const ledger = useLedgerSearch(input)

	const applySearch = (event: React.FormEvent) => {
		event.preventDefault()
		setSearch(searchText.trim())
		setCursor(undefined)
	}

	return (
		<div className="flex flex-col gap-4">
			<form onSubmit={applySearch}>
				<Toolbar>
					<Input
						aria-label="Search the ledger"
						value={searchText}
						onChange={(e) => setSearchText(e.target.value)}
						placeholder="txn_, ch_, py_, re_, pyr_, tr_, du_, TX-, OR-"
						className="w-full font-mono text-xs sm:w-80"
					/>
					<Select
						value={category}
						onValueChange={(v) => {
							setCategory(v)
							setCursor(undefined)
						}}
					>
						<SelectTrigger aria-label="Category" className="w-56">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="all">All categories</SelectItem>
							{Object.entries(CATEGORY_LABELS).map(([value, label]) => (
								<SelectItem key={value} value={value}>
									{label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					<Select
						value={matchStatus}
						onValueChange={(v) => {
							setMatchStatus(v)
							setCursor(undefined)
						}}
					>
						<SelectTrigger aria-label="Match" className="w-40">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="all">Any match</SelectItem>
							<SelectItem value="matched">Matched</SelectItem>
							<SelectItem value="partial">Partial</SelectItem>
							<SelectItem value="unmatched">Unmatched</SelectItem>
							<SelectItem value="not_applicable">Not applicable</SelectItem>
						</SelectContent>
					</Select>
					<Button type="submit" variant="outline">
						Search
					</Button>
					<ToolbarSpacer />
					{ledger.data ? (
						<span className="text-xs text-meta tabular-nums">
							{ledger.data.total} rows
							{wholeLedger ? " (whole ledger)" : " this month"}
						</span>
					) : null}
				</Toolbar>
			</form>

			{ledger.isLoading ? (
				<Panel>
					<SkeletonRows rows={6} />
				</Panel>
			) : ledger.isError ? (
				<QueryErrorLine what="the ledger" error={ledger.error} />
			) : ledger.data && ledger.data.items.length === 0 ? (
				<p className="text-muted-foreground">No balance transaction matches.</p>
			) : (
				<>
					<Panel>
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Created</TableHead>
									<TableHead>Balance transaction</TableHead>
									<TableHead>Category</TableHead>
									<TableHead>Source</TableHead>
									<TableHead className="text-right">Amount</TableHead>
									<TableHead className="text-right">Fee</TableHead>
									<TableHead className="text-right">Net</TableHead>
									<TableHead>Match</TableHead>
									<TableHead>Transaction / orders</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{(ledger.data?.items ?? []).map((row) => (
									<TableRow key={row.id}>
										<TableCell className="whitespace-nowrap text-xs text-muted-foreground">
											{formatInstant(row.created)}
										</TableCell>
										<TableCell>
											<a
												href={`https://dashboard.stripe.com/search?query=${encodeURIComponent(row.stripeId)}`}
												target="_blank"
												rel="noreferrer"
												className={`inline-flex items-center gap-1 ${ID_LINK_CLASS}`}
											>
												{row.stripeId}
												<ExternalLink className="size-3 text-meta" />
											</a>
											<div className="text-xs text-meta">
												{row.currency}
												{row.balanceType === "refund_and_dispute_prefunding"
													? " reserve"
													: ""}
												{row.status === "pending" ? " · pending" : ""}
											</div>
										</TableCell>
										<TableCell>
											<div>{CATEGORY_LABELS[row.category] ?? row.category}</div>
											<div className="font-mono text-xs text-meta">
												{row.subtype}
											</div>
										</TableCell>
										<TableCell>
											<span className="font-mono">
												{row.sourceId ?? EMPTY_VALUE}
											</span>
											{row.description ? (
												<div className="text-xs text-meta">
													{row.description}
												</div>
											) : null}
										</TableCell>
										<TableCell className="text-right">
											<Money cents={row.amount} currency={row.currency} />
										</TableCell>
										<TableCell className="text-right text-muted-foreground">
											<Money cents={row.fee} currency={row.currency} />
										</TableCell>
										<TableCell className="text-right">
											<Money cents={row.net} currency={row.currency} />
										</TableCell>
										<TableCell>
											<div className="flex flex-col items-start gap-0.5">
												{matchStatusView(row.matchStatus)}
												{row.matchMethod || row.amountCheck === "mismatch" ? (
													<span className="text-xs text-meta">
														{row.matchMethod ?? ""}
														{row.amountCheck === "mismatch"
															? ` · expected ${formatMinor(row.expectedAmount, row.sourceCurrency ?? row.currency)}`
															: ""}
													</span>
												) : null}
											</div>
										</TableCell>
										<TableCell>
											<div className="flex flex-col gap-0.5">
												{row.matchedTransactionId ? (
													<Link
														to="/transactions/$transactionId"
														params={{ transactionId: row.matchedTransactionId }}
														className={ID_LINK_CLASS}
													>
														{row.matchedTransactionNumber ??
															row.matchedTransactionId}
													</Link>
												) : row.metaTransactionNumber ? (
													<span className="font-mono">
														{row.metaTransactionNumber}
													</span>
												) : null}
												{row.allocations.map((allocation) => (
													<span
														key={allocation.orderId}
														className="flex items-baseline gap-2 text-xs"
													>
														<Link
															to="/orders/$orderId"
															params={{ orderId: allocation.orderId }}
															className={ID_LINK_CLASS}
														>
															{allocation.orderNumber}
														</Link>
														<Money
															cents={allocation.net}
															currency={allocation.currency}
															className="text-muted-foreground"
														/>
													</span>
												))}
											</div>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</Panel>
					{ledger.data?.nextCursor ? (
						<div>
							<Button
								variant="outline"
								size="sm"
								onClick={() => setCursor(ledger.data?.nextCursor ?? undefined)}
							>
								Next page
							</Button>
						</div>
					) : null}
				</>
			)}
		</div>
	)
}
